import { useState } from 'react'
import {
  Text,
  Badge,
  Button,
  Collapse,
  GreyGrey10,
  GreyGrey30,
  GreyGrey60,
  GreyGrey80,
  IconChevronDown,
  IconReceiptCredit,
  IndigoIndigo60,
  Link,
  Primary13SemiBold,
  Primary14SemiBold,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import { inflect } from 'inflection'
import { toMoney } from '@sequencehq/utils'
import type { Currency } from '@sequencehq/api'
import { toAppliedCreditNoteAmount } from '@sequencehq/invoice-content'
import { AppliedCreditNote } from 'Invoices/types'
import { negate } from 'Invoices/components/CreateEditShared/SectionLineItems/invoiceSettlement'
import { sumAppliedCreditNotes } from 'Invoices/utils'

type Props = {
  appliedCreditNotes: AppliedCreditNote[]
  currency: Currency
}

/**
 * Card summarising the credit notes already applied to the invoice, shown
 * between the items-to-credit table and the totals so users issuing a
 * subsequent credit note can see prior credits without leaving the drawer.
 */
export const PreviouslyCreditedSection = ({
  appliedCreditNotes,
  currency
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(true)

  if (appliedCreditNotes.length === 0) {
    return null
  }

  const asNegativeMoney = (value: string) =>
    toMoney({ currency, value: negate(value) })

  const totalCredited = sumAppliedCreditNotes(appliedCreditNotes)

  const countLabel = `${appliedCreditNotes.length} ${inflect(
    'credit note',
    appliedCreditNotes.length
  )}`

  return (
    <Box
      border={`1px solid ${GreyGrey30}`}
      borderRadius="8px"
      data-testid="issue-credit-note.previously-credited"
    >
      <Flex
        alignItems="center"
        gap="8px"
        padding="8px 12px"
        backgroundColor={GreyGrey10}
        borderRadius="8px"
      >
        <IconReceiptCredit color={GreyGrey60} />
        <Text {...Primary13SemiBold} color={GreyGrey80}>
          Previously credited
        </Text>
        <Badge sentiment="neutral" size="sm">
          {countLabel}
        </Badge>
        <Flex flex="1" />
        <Text
          {...Primary13SemiBold}
          color={GreyGrey80}
          data-testid="issue-credit-note.previously-credited.total"
        >
          {asNegativeMoney(totalCredited)}
        </Text>
        <Button
          variant="unstyled"
          style={{ padding: '0px', minWidth: '16px' }}
          color={GreyGrey60}
          aria-label={
            isExpanded
              ? 'Collapse previously credited'
              : 'Expand previously credited'
          }
          onClick={() => setIsExpanded(previous => !previous)}
          data-testid="issue-credit-note.previously-credited.toggle"
        >
          <Center
            boxSize="16px"
            transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
            transition="transform 250ms"
          >
            <IconChevronDown color="inherit" />
          </Center>
        </Button>
      </Flex>
      <Collapse in={isExpanded}>
        {appliedCreditNotes.map((creditNote, index) => (
          <Flex
            key={`${creditNote.id}-${index}`}
            gap="12px"
            padding="12px"
            alignItems="center"
            borderTop={`1px solid ${GreyGrey30}`}
            data-testid={`issue-credit-note.previously-credited.${creditNote.id}`}
          >
            <Text
              as={Link.Unstyled}
              to={`/credit-notes/${creditNote.id}`}
              flex="1"
              {...Primary14SemiBold}
              color={IndigoIndigo60}
            >
              {creditNote.creditNoteNumber ?? 'Credit note'}
            </Text>
            <Text {...Primary14SemiBold} color={GreyGrey80}>
              {asNegativeMoney(toAppliedCreditNoteAmount(creditNote))}
            </Text>
          </Flex>
        ))}
      </Collapse>
    </Box>
  )
}
